import { alpha } from "@mui/material/styles";
import {
  LEGACY_SIZE_ALIASES,
  LEGACY_VARIANT_ALIASES,
} from "../buttonVocabulary";

const focusRing = (theme) => ({
  outline: `3px solid ${theme.palette.accent?.butterscotch}`,
  outlineOffset: "2px",
});

const muiSizeReset = {
  "&.MuiButton-sizeSmall, &.MuiButton-sizeMedium, &.MuiButton-sizeLarge": {},
};

const submitBlockLayout = {
  width: "100%",
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
  padding: "12px 20px",
  fontSize: "1rem",
  borderRadius: "12px",
  minHeight: "48px",
};

function primarySubmitColors({ theme }) {
  return {
    backgroundColor: theme.palette.button.primaryBackground,
    color: theme.palette.button.primaryColor,
    "&:hover": {
      backgroundColor: theme.palette.button.primaryHoverBackground,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.button.greenDisabledBackground,
      color: theme.palette.button.greenDisabledColor,
    },
    "&:focus-visible": focusRing(theme),
  };
}

const { ctaPill: _ctaPill, ...intentStyleAliases } = LEGACY_VARIANT_ALIASES;

export const buttonRecipe = {
  intents: {
    a11yLang: ({ theme }) => ({
      minWidth: "auto",
      height: "24px",
      padding: "8px 20px",
      fontSize: "14px",
      fontWeight: 600,
      borderRadius: "6px",
      textTransform: "none",
      lineHeight: 1,
      backgroundColor: alpha(theme.palette.common?.white , 0.06),
      border: `1px solid ${alpha(theme.palette.common?.white , 0.12)}`,
      color: theme.palette.common?.white ,
      "&:hover": { backgroundColor: alpha(theme.palette.common?.white , 0.12) },
      ...muiSizeReset,
    }),
    a11yLangActive: ({ theme }) => ({
      minWidth: "auto",
      height: "24px",
      padding: "8px 20px",
      fontSize: "14px",
      fontWeight: 600,
      borderRadius: "6px",
      textTransform: "none",
      lineHeight: 1,
      backgroundColor: theme.palette.accent?.butterscotch ,
      color: theme.palette.accessibility.langActiveText,
      border: "1px solid transparent",
      "&:hover": { backgroundColor: theme.palette.accent?.butterscotch  },
      ...muiSizeReset,
    }),
    a11yTrigger: ({ theme }) => ({
      width: "33.6px",
      height: "24px",
      minWidth: "auto",
      padding: 0,
      backgroundColor: alpha(theme.palette.common?.white , 0.06),
      border: `1px solid ${alpha(theme.palette.common?.white , 0.12)}`,
      borderRadius: "3px",
      color: theme.palette.common?.white ,
      "&:hover": { backgroundColor: alpha(theme.palette.common?.white , 0.12) },
      "&:focus-visible": {
        outline: "2px solid",
        outlineColor: theme.palette.accent?.butterscotch ,
        outlineOffset: "2px",
      },
      ...muiSizeReset,
    }),

    primary: ({ theme }) => ({
      fontWeight: 600,
      backgroundColor: theme.palette.button.primaryBackground,
      color: theme.palette.button.primaryColor,
      "&:hover": {
        backgroundColor: theme.palette.button.primaryHoverBackground,
      },
      "&:focus-visible": focusRing(theme),
      "& .MuiButton-endIcon > *:nth-of-type(1)": {
        fontSize: "1rem",
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.button.disabledBackground,
        color: theme.palette.button.disabledColor,
      },
    }),
    secondary: ({ theme }) => ({
      backgroundColor: theme.palette.button.secondaryBackground,
      border: `1.5px solid ${theme.palette.button.secondaryBorder}`,
      color: theme.palette.button.secondaryColor,
      "&:hover": {
        backgroundColor: theme.palette.button.secondaryHoverBackground,
        color: theme.palette.button.secondaryHoverColor,
        border: `1.5px solid ${theme.palette.button.secondaryHoverColor}`,
      },
      "&:focus-visible": focusRing(theme),
      "& .MuiButton-endIcon > *:nth-of-type(1)": {
        fontSize: "1rem",
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.button.disabledBackground,
        color: theme.palette.button.disabledColor,
        borderColor: theme.palette.button.disabledBackground,
      },
    }),
    tertiary: ({ theme }) => ({
      border: "none",
      color: theme.palette.primary.interactive,
      borderRadius: 0,
      minWidth: 0,
      ...muiSizeReset,
      "&.MuiButton-sizeSmall, &.MuiButton-sizeMedium, &.MuiButton-sizeLarge": {
        padding: 0,
      },
      "&:hover": {
        color: theme.palette.primary.interactiveHover,
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
      "&:focus-visible": focusRing(theme),
      "& .MuiButton-endIcon > *:nth-of-type(1)": {
        fontSize: "1rem",
      },
      "&.Mui-disabled": {
        color: theme.palette.text.disabled,
      },
    }),
    link: ({ theme }) => ({
      border: "none",
      color: theme.palette.primary.interactive,
      borderRadius: 0,
      minWidth: 0,
      fontWeight: 700,
      textDecoration: "underline",
      ...muiSizeReset,
      "&.MuiButton-sizeSmall, &.MuiButton-sizeMedium, &.MuiButton-sizeLarge": {
        padding: 0,
        fontSize: "1rem",
      },
      "&:hover": {
        color: theme.palette.primary.interactiveHover,
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
      "&:focus-visible": focusRing(theme),
      "& .MuiButton-endIcon > *:nth-of-type(1)": {
        fontSize: "1rem",
      },
      "&.Mui-disabled": {
        color: theme.palette.text.disabled,
      },
    }),
    danger: ({ theme }) => ({
      backgroundColor: theme.palette.button.dangerBackground,
      color: theme.palette.button.dangerColor,
      "&:hover": {
        backgroundColor: theme.palette.button.dangerHoverBackground,
      },
      "&:focus-visible": focusRing(theme),
      "& .MuiButton-endIcon > *:nth-of-type(1)": {
        fontSize: "1rem",
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.button.disabledBackground,
        color: theme.palette.button.disabledColor,
      },
    }),
    dangerText: ({ theme }) => ({
      border: "none",
      backgroundColor: "transparent",
      color: theme.palette.pills.error.text,
      justifyContent: "flex-start",
      minWidth: 0,
      ...muiSizeReset,
      "&.MuiButton-sizeSmall, &.MuiButton-sizeMedium, &.MuiButton-sizeLarge": {
        padding: 0,
      },
      "& .MuiButton-startIcon": { color: "inherit", marginLeft: 0 },
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
      "&:focus-visible": focusRing(theme),
      "&.Mui-disabled": {
        color: theme.palette.text.disabled,
      },
    }),
    filterChip: ({ theme }) => ({
      minHeight: "24px",
      borderRadius: "12px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.neutral.surface
          : theme.palette.background.tertiaryWhite,
      color: theme.palette.neutral.headings,
      fontWeight: theme.typography.fontWeightMedium,
      ...muiSizeReset,
      "&.MuiButton-sizeSmall, &.MuiButton-sizeMedium, &.MuiButton-sizeLarge": {
        padding: theme.spacing(1.5, 3),
      },
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:focus-visible": focusRing(theme),
    }),
    navBack: ({ theme }) => ({
      flexShrink: 0,
      minWidth: 0,
      gap: theme.spacing(0.5),
      borderRadius: "6px",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.text.white,
      fontWeight: theme.typography.fontWeightMedium,
      boxShadow: "none",
      ...muiSizeReset,
      "&.MuiButton-sizeSmall, &.MuiButton-sizeMedium, &.MuiButton-sizeLarge": {
        padding: theme.spacing(0.5, 1.5),
      },
      "& .MuiButton-startIcon": {
        marginLeft: 0,
        marginRight: theme.spacing(0.5),
      },
      "& .MuiButton-startIcon svg": { fontSize: theme.spacing(3) },
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: "none",
      },
      "&:focus-visible": focusRing(theme),
    }),
  },

  intentAliases: intentStyleAliases,

  sizes: {
    wizard: {
      fontSize: "0.875rem",
      padding: "0.75rem 2rem",
      minHeight: "24px",
    },
    card: {
      fontSize: "1rem",
      padding: "0.75rem 1.25rem",
      borderRadius: "12px",
      minHeight: "24px",
    },
    compact: {
      fontSize: "0.875rem",
      lineHeight: "20px",
      padding: "0.5rem 0.75rem",
      borderRadius: "8px",
      minHeight: "24px",
    },
    prominent: {
      fontSize: "1rem",
      lineHeight: "24px",
      padding: "0.75rem 1.25rem",
      borderRadius: "12px",
      minHeight: "48px",
    },
    submit: submitBlockLayout,
  },

  sizeAliases: LEGACY_SIZE_ALIASES,

  compounds: [
    {
      props: { variant: "primary", size: "wizard" },
      legacyProps: [{ variant: "primary", size: "cta" }],
      style: { borderRadius: "8px" },
    },
    {
      props: { variant: "secondary", size: "wizard" },
      legacyProps: [{ variant: "secondary", size: "cta" }],
      style: { borderRadius: "10px" },
    },
    {
      props: { variant: "primary", size: "submit" },
      style: primarySubmitColors,
    },
  ],

  legacyCompounds: [
    {
      props: { variant: "ctaPill" },
      style: ({ theme }) => ({
        ...submitBlockLayout,
        ...primarySubmitColors({ theme }),
      }),
    },
  ],
};
